// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import loadMessages from "@insite/client-framework/Store/Data/Messages/Handlers/LoadMessages";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import translate from "@insite/client-framework/Translate";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Typography from "@store/MobiusOverrides/Typography/Typography";
import { countBubblePositionStyles, countBubbleStyles } from "@store/Themes/common/mixins/countBubble";
import useExpandProps, { ExpandStyles } from "@store/Framework/Common/Hooks/useExpandProps";
import usePersistentDataView from "@store/Framework/Common/Hooks/usePersistentDataView";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { useSession, useSettingsCollection } from "@store/Framework/Store/Context/ContextHooks";
import { useMessagesDataView } from "@store/Framework/Store/Data/Messages/MessagesHook";
import React, { FC, useEffect, useMemo, useState } from "react";
import { css } from "styled-components";

const messagesParameter = {};

export interface AccountMessagesCountPresentationProps extends ExpandStyles<AccountMessagesCountStyles> {
    /** Position configuration for the count bubble (e.g., top, right, bottom, left positioning) */
    position?: (string | null)[];
    /** Override flag to show/hide the account messages notification count */
    overrideShowAccountMessagesNotificationCount?: boolean;
}

export interface AccountMessagesCountProps extends AccountMessagesCountPresentationProps {}

export interface AccountMessagesCountStyles {
    messagesCountText?: TypographyPresentationProps;
}

export const accountMessagesCountStyles: AccountMessagesCountStyles = {
    messagesCountText: {
        css: css`
            ${countBubbleStyles()}
        `,
    },
};

const AccountMessagesCount: FC<AccountMessagesCountProps> = ({
    extendedStyles,
    overridedStyles,
    position,
    overrideShowAccountMessagesNotificationCount,
}) => {
    const styles = useExpandProps(accountMessagesCountStyles, extendedStyles, overridedStyles);
    const dispatch = useAppDispatch();
    const [messagesCount, setMessagesCount] = useState(0);
    const session = useSession();
    const isAuthenticated = session && session.isAuthenticated && !session.isGuest;
    const baseMessagesDataView = useMessagesDataView(messagesParameter);
    const { value: messages, isLoading } = usePersistentDataView(baseMessagesDataView);
    const shouldLoadMessages = !isLoading && !messages;
    const { extensionWebsiteSettings } = useSettingsCollection();
    const showAccountMessagesNotificationCount =
        extensionWebsiteSettings && extensionWebsiteSettings.showAccountMessagesNotificationCount;

    useEffect(() => {
        if (shouldLoadMessages && isAuthenticated) {
            dispatch(loadMessages(messagesParameter));
        }
    }, [shouldLoadMessages, isAuthenticated]);

    useEffect(() => {
        const _count = messages?.filter(msg => !msg.isRead).length || 0;
        setMessagesCount(_count);
    }, [messages]);

    const messagesCountStyles = useMemo(() => {
        if (!position) {
            return styles.messagesCountText;
        }
        return mergeProps<AccountMessagesCountStyles["messagesCountText"]>(styles.messagesCountText, {
            css: css`
                ${countBubblePositionStyles(position)}
            `,
        });
    }, [position, styles.messagesCountText]);

    const shouldCheckHeaderMessagesWidget =
        !!messagesCount && !overrideShowAccountMessagesNotificationCount && !!showAccountMessagesNotificationCount;
    const hasHeaderMessagesWidget = useAppSelector(state =>
        shouldCheckHeaderMessagesWidget
            ? getWidgetsByPageId(state, getHeader(state).id).some(w => w.type === "Header/HeaderMessages")
            : false,
    );

    if (!messagesCount) {
        return null;
    }

    if (
        !overrideShowAccountMessagesNotificationCount &&
        (!showAccountMessagesNotificationCount || hasHeaderMessagesWidget)
    ) {
        return null;
    }

    return (
        <Typography {...messagesCountStyles}>
            {messagesCount}
            <VisuallyHidden>{translate("unread messages")}</VisuallyHidden>
        </Typography>
    );
};

export default AccountMessagesCount;
